import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import queryString from 'query-string'

import { tagHotjarRecording, sendEventToIntercom } from '../../helpers/util'
import { logErrorOnServer } from '../../helpers/serverLogging'
import useRequest from '../../hooks/useRequest'
import useAuth from '../../hooks/useAuth'
import AppRoutes from '../../routes/appRoutes'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import MainHeading from '../../components/MainHeading/MainHeading'
import CardInfo from '../../components/CardInfo/CardInfo'
import Button from '../../components/Button/Button'
import InputText from '../../components/InputText/InputText'
import DropDown from '../../components/DropDown/DropDown'
import CellLocation from '../../components/Table/components/CellLocation'
import InputNumber from '../../components/InputNumber/InputNumber'
import InputDate from '../../components/InputDate/InputDate'
import { CheckIcon } from '@heroicons/react/outline'
import { useFormik } from 'formik'
import {
    initialValues,
    validationSchema,
} from '../../forms/CreateRentalRequestForm'
import useGlobalLoading from '../../hooks/useGlobalLoading'
import useAlert from '../../hooks/useAlert'
import useScreen from '../../hooks/useScreen'
import clsx from 'clsx'

const logError = logErrorOnServer('New Rental Request')

const CustomDivider = () => {
    return (
        <div className="w-full h-px bg-grayLines mt-2 mb-6 lg:block hidden" />
    )
}

const RentalRequestPage = props => {
    const { location, history } = props

    const [equipmentSFId, setEquipmentSFId] = useState(undefined)
    const [error, setError] = useState('')
    const [isFormDirty, setIsFormDirty] = useState(false)
    const {
        handleCreateRentalRequest,
        changeNeedsUpdate,
        needsUpdateDashboard,
    } = useRequest()
    const { user, impersonatedBy } = useAuth()
    const { changeLoading } = useGlobalLoading()
    const { handleChangeAlert, handleChangeOpen } = useAlert()
    const { isMobile } = useScreen()

    const shiptoLocations = user?.locationPreferences

    useEffect(() => {
        //Get the passed in equipment if we have one
        const values = queryString.parse(location.search)
        const equipmentId = values.equipmentId ? values.equipmentId : ''
        if (equipmentId) {
            setEquipmentSFId(equipmentId)
        }
        tagHotjarRecording('Rental Request')
    }, [])

    const onCreatedRentalRequestSuccess = response => {
        sendEventToIntercom('successful-rental-quote-request')
        if (response.status === 200) {
            changeNeedsUpdate({
                dashboard: needsUpdateDashboard,
                request: true,
            })
            changeLoading(false)
            handleChangeAlert({
                title: 'Rental Request Created ',
                message: 'We will contact you shortly about your request.',
                type: 'success',
                textOk: 'View Request',
                textCancel: 'Return to Dashboard',
                onCancel: () => history?.push(AppRoutes.dashboard()),
                onDone: () => {
                    const id = response?.data?.response?.request?.salesforceId
                    history?.push(AppRoutes.viewRentalRequest(id))
                    handleChangeOpen(false)
                },
            })
        }
    }

    const onCreatedRentalRequestError = resp => {
        tagHotjarRecording('API Error')
        const {
            errors,
            message = 'Please check your internet connection and try again.',
        } = resp?.response?.data ?? {}
        const errorMessage =
            errors &&
                Array.isArray(errors.whatIsNeeded) &&
                errors.whatIsNeeded[0].length > 0
                ? errors.whatIsNeeded[0]
                : errors &&
                    Array.isArray(errors.howMany) &&
                    errors.howMany[0].length > 0
                    ? errors.howMany[0]
                    : message
        changeLoading(false)
        setError(errorMessage)
        console.log(error)
        logError(errorMessage)
    }

    const onSubmit = values => {
        const rentalRequestInfo = {
            equipmentSFId,
            userId: user?.id,
            ...values,
        }
        changeLoading(true)
        handleCreateRentalRequest(
            rentalRequestInfo,
            onCreatedRentalRequestSuccess,
            onCreatedRentalRequestError,
        )
    }

    const {
        getFieldProps,
        errors,
        touched,
        handleSubmit,
        isValid,
        isSubmitting,
        setSubmitting,
        setFieldValue,
        values,
        dirty,
        resetForm,
    } = useFormik({
        initialValues: {
            ...initialValues,
            fromDate: new Date(),
            toDate: new Date(),
        },
        validationSchema,
        onSubmit: formValues => {
            setSubmitting(true)
            onSubmit(formValues)
            setSubmitting(false)
        },
    })

    // Track dirty state changes
    useEffect(() => {
        setIsFormDirty(dirty)
    }, [dirty])

    const formattedShiptos = shiptoLocations?.map(item => {
        return {
            ...item,
            locationToShow: (
                <CellLocation
                    isInCard
                    label={item?.label}
                    address={item?.address}
                />
            ),
        }
    })

    const hasTouched = Object.keys(touched)?.length > 0

    const handleCancelClick = () => {
        if (isFormDirty) {
            // Reset the form if there's data
            resetForm()
            setIsFormDirty(false)
        }
    }

    const renderButtons = () => {
        return (
            <div className="flex-grow lg:w-auto w-full right-0 left-0 h-auto flex items-center justify-center gap-x-2">
                <Button
                    key="cancelRentalRequest"
                    text="Cancel"
                    onClick={handleCancelClick}
                    disabled={!isFormDirty}
                    variant="tertiary"
                    className="lg:w-auto w-full flex-grow"
                />
                <Button
                    key="submitRentalRequest"
                    text="Submit Request"
                    type="submit"
                    disabled={
                        isSubmitting ||
                        !isValid ||
                        !isFormDirty ||
                        impersonatedBy === 'salesperson'
                    }
                    className="lg:w-auto w-full flex-grow"
                    onClick={handleSubmit}
                    variant="primary"
                    icon={
                        <CheckIcon className="text-yellow stroke-current stroke-2 w-5 h-5 mr-2" />
                    }
                />
            </div>
        )
    }

    const renderForm = () => {
        return (
            <div className="flex flex-col items-start justify-start w-full h-auto py-2 lg:gap-y-0">
                <InputText
                    {...getFieldProps('whatIsNeeded')}
                    type="textarea"
                    label="What do you need?"
                    isRequired
                    placeholder="Please tell us the details of your request"
                    additionalInputClass="h-20"
                    error={touched['whatIsNeeded'] && errors['whatIsNeeded']}
                />
                <CustomDivider />
                <div className="w-full flex lg:flex-row flex-col items-start justify-between lg:gap-x-8 lg:gap-y-0">
                    <DropDown
                        customRender={({ label, address }) => (
                            <CellLocation
                                isInCard
                                label={label}
                                address={address}
                            />
                        )}
                        {...getFieldProps('deliverToAccountId')}
                        data={formattedShiptos}
                        keyId="id"
                        keyToShow="locationToShow"
                        keyToFind="address"
                        setSelectedValue={selected =>
                            setFieldValue('deliverToAccountId', selected?.value)
                        }
                        selected={
                            shiptoLocations?.find(
                                s => s?.value === values?.deliverToAccountId,
                            ) || null
                        }
                        name="selectLocation"
                        placeholder="Select Location"
                        label="Where do you need it?"
                        className="flex-grow w-full lg:rowSimilar"
                        isSmall={!values?.deliverToAccountId}
                        required
                        error={
                            (hasTouched && errors['deliverToAccountId']) || ''
                        }
                    />
                    <InputNumber
                        onChange={value => setFieldValue('howMany', value)}
                        initialValue={values?.howMany}
                        error={hasTouched && errors['howMany']}
                        className="flex-grow w-full lg:rowSimilar lg:mb-0 mb-4"
                        label="How many?"
                        required
                    />
                </div>
                <CustomDivider />
                <div className="flex lg:flex-row flex-col items-center justify-between w-full h-auto lg:gap-x-8 lg:gap-y-0 gap-y-4">
                    <InputDate
                        onChange={value => setFieldValue('fromDate', value)}
                        initialValue={values?.fromDate}
                        error={errors['fromDate']}
                        label="From:"
                        className="lg:mb-0 mb-4"
                    />
                    <InputDate
                        onChange={value => setFieldValue('toDate', value)}
                        initialValue={values?.toDate}
                        error={errors['toDate']}
                        label="To:"
                    />
                </div>
            </div>
        )
    }

    return (
        <ContentLayout>
            <MainHeading
                isTabletViewPort
                title="Rental Request"
                subtitle="What type of equipment are you looking for?"
                actions={!isMobile ? [renderButtons()] : null}
            />
            <div
                className={clsx(
                    'w-full h-full overflow-auto flex flex-col items-center justify-start max-w-full max-h-full lg:px-6 lg:py-12 px-4 py-4',
                    isMobile && 'mb-24',
                )}
            >
                <div className="lg:w-220 w-full h-auto">
                    <CardInfo
                        isOverflowVisible
                        title="Rental Details"
                        subTitle="Please tell us what you need"
                        content={
                            <form
                                onSubmit={handleSubmit}
                                className="w-full h-auto"
                            >
                                {renderForm()}
                            </form>
                        }
                    />
                </div>
            </div>

            {isMobile && (
                <div className="w-full z-100 bg-white p-4 border-t border-grayLines h-auto absolute bottom-0 right-0">
                    {renderButtons()}
                </div>
            )}
        </ContentLayout>
    )
}

RentalRequestPage.propTypes = {
    location: PropTypes.shape({
        search: PropTypes.string,
        state: PropTypes.shape({
            source: PropTypes.string,
        }),
    }),
    history: PropTypes.shape({
        push: PropTypes.func,
    }),
}

export default RentalRequestPage
